import { createSelector } from '@reduxjs/toolkit';

import { getImmutableCompatible } from 'src/utils/immutableUtils';

import { touristsSelector } from 'src/store/tourists/selectors';

import { isEmpty } from 'lodash';
import { name } from './slice';
import type { TouristsValidationState } from './types';
import * as transform from './transform';

export const touristsValidationSelector = (state): TouristsValidationState =>
  getImmutableCompatible(state, name);

/**
 * Валидация начата
 */
export const inProgressSelector = createSelector(
  touristsValidationSelector,
  (state) => state.fetchStatus === 'LOADING',
);

/**
 * По флагу valid понимаем, прошли бековскую валидацию или нет
 */
export const isValidSelector = createSelector(
  touristsValidationSelector,
  (state) => state.isValid,
);

/**
 * Если ручка /tourists/validate упала
 */
export const isRequestFailedSelector = createSelector(
  touristsValidationSelector,
  (state) => state.fetchStatus === 'FAILED',
);

/**
 * Объект с ошибками валидации
 */
export const errorsSelector = createSelector(
  touristsValidationSelector,
  (state) => state.errors,
);

/**
 * Объект с общими ошибками валидации
 */
export const _commonErrorsSelector = createSelector(
  touristsValidationSelector,
  (state) => transform.getFlatErrors(state.errors.common),
);

/**
 * Объект с ошибками валидации туристов
 */
export const touristsErrorsSelector = createSelector(
  touristsValidationSelector,
  (state) => state.errors.tourists,
);

/**
 * Массив с ошибками конкретного туриста
 */
export const touristErrorsSelector = createSelector(
  [touristsErrorsSelector, (state, index) => index],
  (touristsErrors, touristsIndex) =>
    transform.getTouristValidationErrorsList(
      touristsErrors[touristsIndex] || {},
    ),
);

export const touristIsValidSelector = createSelector(
  [touristsErrorsSelector, (_, index) => index],
  (touristsErrors, touristsIndex) => isEmpty(touristsErrors[touristsIndex]),
);

/**
 * - Берем туристов со страницы чекаута
 * - трансформируем в формат понятный ручке /tourists/validate
 * - оставляем только полностью заполненных туристов
 */
export const touristsValidationPayloadSelector = createSelector(
  touristsSelector,
  (tourists) => tourists.map(transform.getTouristPayload),
);

/**
 * Сравниваем количестно полностью заполненных туристов с числом туристов, которое ожидается в пакете
 */
export const showCommonErrorsSelector = createSelector(
  [touristsValidationPayloadSelector, (state, count: number) => count],
  (tourists, packageTouristsCount) => tourists.length === packageTouristsCount,
);

/**
 * Если все туристы в пакете заполнены, показываем общие ошибки валидации (если они есть)
 */
export const commonErrorsSelector = createSelector(
  [showCommonErrorsSelector, _commonErrorsSelector],
  (showCommonErrors, commonErrors) => (showCommonErrors ? commonErrors : []),
);
